import { defineStore } from 'pinia';

import {
  ShopDetailResponse,
  ShopPaymentCategoryForm,
  ShopRepository,
  ShopSplitPaymentDetailResponse,
} from '@/repositories/generated';
import {
  ShopPaymentCategoriesArrayDatum,
  ShopSplitPaymentEditForm,
} from '@/validations/shop-split-payment';

type TEditShopSplitPaymentState = {
  form: ShopSplitPaymentEditForm | undefined;
  initData: ShopSplitPaymentDetailResponse | undefined;
  initShopData: ShopDetailResponse | undefined;
};

// 画面上で削除された支払区分をここで削除したり、予約された支払区分を設定したりする
const editPaymentCategory = (
  form: ShopPaymentCategoriesArrayDatum[],
  initData: ShopSplitPaymentDetailResponse,
): Array<ShopPaymentCategoryForm> => form
  .map((it, index) => {
    const initInfo = initData.shopPaymentCategories[index];
    // 予約済の場合は編集不可なので元のデータをそのまま返す
    if (it.categoryStatus === 'exist' && initInfo != null && initInfo.reservationInfo?.id != null) {
      return {
        reservationApplyDate: initInfo.reservationInfo.reservationApplyDate,
        applicableType: 'RESERVATION',
        feeCategory: initInfo.feeCategory,
        paymentCategories: initInfo.reservationInfo.reservedPaymentCategories,
        bonusCombinedPaymentNumber: initInfo.reservationInfo.reservedBonusCombinedPaymentNumber,
        installmentPaymentNumber: initInfo.reservationInfo.reservedInstallmentPaymentNumber,
      };
    }
    // 新規追加or変更の場合は、formのデータを返す
    if (it.categoryStatus === 'add' || it.categoryStatus === 'exist') {
      return {
        reservationApplyDate: it.applicableType === 'RESERVATION' ? it.reservationApplyDate?.toISOString() : undefined,
        applicableType: it.applicableType,
        feeCategory: it.feeCategory,
        paymentCategories: it.paymentCategory,
        bonusCombinedPaymentNumber: it.bonusCombinedPaymentNumber || undefined, // 空文字の場合はundefinedに変換
        installmentPaymentNumber: it.installmentPaymentNumber || undefined, // 空文字の場合はundefinedに変換
      };
    }
    // 削除の場合はundefinedを返して最後にフィルタして取り除く
    return undefined;
  })
  .filter((it) => it != null) as Array<ShopPaymentCategoryForm>;

export const useEditShopSplitPaymentStore = defineStore('editShopSplitPaymentShop', {
  state: (): TEditShopSplitPaymentState => ({
    form: undefined,
    initData: undefined,
    initShopData: undefined,
  }),
  actions: {
    async editShopSplitPaymentShop() {
      const {
        form,
        initData,
        initShopData,
      } = this;

      if (!(form && initData && initShopData)) return;

      await ShopRepository.edit1({
        id: initData.id,
        requestBody: {
          ...form,
          bonusPaymentMonths: form.bonusPaymentMonths ?? [],
          shopPaymentCategories: editPaymentCategory(form.shopPaymentCategories, initData),
          lastUpdatedAt: initShopData.shopDetailBasicInfo.updatedAt,
        },
      });
    },
    async setInitData(id: string) {
      this.$reset();

      this.initData = await ShopRepository.detail1({ id });
      this.initShopData = await ShopRepository.detail({ id });
    },
    setUiForm(form: ShopSplitPaymentEditForm) {
      this.form = { ...form };
    },
    $resetForm() {
      this.form = undefined;
    },
  },
});
